/**
 * depreciationHelper.js — pure depreciation engine for the Fixed Assets module.
 *
 * No Firestore, no React — just math, so it is fully unit-testable and reused by
 * the Asset Register (schedule preview), the Depreciation Run page (batch build),
 * and the Disposal page (current NBV).
 *
 * Methods:
 *   - 'straight_line'    : (cost − salvage) / usefulLifeYears, charged evenly.
 *   - 'reducing_balance' : opening NBV × rate% per year (FBR / WDV basis).
 *   - 'none'             : never depreciates (e.g. Land).
 *
 * Conventions (locked 2026-05-29):
 *   - First period starts the MONTH of inServiceDate (no day-level pro-rata).
 *   - Depreciation never drives NBV below the floor: salvageValue for
 *     straight-line, 0 for reducing-balance (salvage respected if provided).
 *
 * Asset Appreciation (upward revaluation, optional module):
 *   - The original cost is never changed. The increase is kept separately
 *     (appreciationAmount + dated appreciationEntries), so
 *     book value = cost + appreciation − accumulated depreciation.
 *   - Reducing balance: the rate runs on the higher book value from the month
 *     each appreciation is dated (earlier months use the value without it).
 *   - Straight line: each appreciation is spread over the asset's REMAINING
 *     life from the month it is dated (if the life is already over, that
 *     appreciation is not depreciated and stays in the book value).
 *   - appreciationDepreciation = the part of the accumulated depreciation that
 *     was charged on the appreciation, so the tax (FBR) figures stay on cost.
 *   - Assets with NO appreciation run through exactly the old code path.
 */

// FBR Third-Schedule default annual rates (%) per category key — used as
// defaults when seeding categories. Reducing-balance basis.
export const FBR_DEFAULT_RATES = {
  land: 0,
  building: 10,
  furniture: 15,
  office_equipment: 15,
  vehicles: 15,
  plant_machinery: 15,
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const toDate = (d) => {
  if (!d) return null;
  if (d instanceof Date) return d;
  // Accept 'YYYY-MM-DD' or ISO strings.
  const parsed = new Date(d);
  return isNaN(parsed.getTime()) ? null : parsed;
};

// Months elapsed from `start` up to and including `asOf` month.
// Same month → 0 "before" (the in-service month itself counts as month 1 when
// the run period is that month). Used only to decide whether an asset is in
// service yet for a given run period.
const isInServiceBy = (asset, asOfDate) => {
  const inService = toDate(asset.inServiceDate || asset.acquisitionDate);
  const asOf = toDate(asOfDate);
  if (!inService || !asOf) return true; // be permissive if dates missing
  // In service if asOf is in the same month or later than inServiceDate.
  const aY = asOf.getFullYear();
  const aM = asOf.getMonth();
  const iY = inService.getFullYear();
  const iM = inService.getMonth();
  return aY > iY || (aY === iY && aM >= iM);
};

// 'YYYY-MM-DD' (or a Date) → absolute month number (year*12 + month0).
const monthIdx = (d) => {
  if (!d) return null;
  if (typeof d === 'string') {
    const m = /^(\d{4})-(\d{2})/.exec(d);
    if (m) return Number(m[1]) * 12 + (Number(m[2]) - 1);
  }
  const x = toDate(d);
  return x ? x.getFullYear() * 12 + x.getMonth() : null;
};

/** Total appreciation on an asset (0 when none). */
export const appreciationOf = (asset) => Math.max(0, round2(asset?.appreciationAmount));
export const hasAppreciation = (asset) => appreciationOf(asset) > 0;

/** Depreciation already charged on the appreciation part (never above the appreciation). */
export const appreciationDepreciationOf = (asset) =>
  Math.min(appreciationOf(asset), Math.max(0, round2(asset?.appreciationDepreciation)));

/** Book value = cost + appreciation − accumulated depreciation. */
export const bookValueOf = (asset) => round2(
  (Number(asset?.acquisitionCost) || 0) + appreciationOf(asset) - (Number(asset?.accumulatedDepreciation) || 0)
);

/** Value on original cost only (for tax statements): cost − depreciation charged on cost. */
export const costBasisValueOf = (asset) => round2(
  (Number(asset?.acquisitionCost) || 0)
  - ((Number(asset?.accumulatedDepreciation) || 0) - appreciationDepreciationOf(asset))
);

/**
 * Straight-line split of an asset's appreciation entries:
 *   entries: the ones with life left at their date → { amount, rem (months), idx }
 *   depreciable: their total; nonDepreciable: the rest (life already over).
 */
const slAppreciationParts = (asset) => {
  const total = appreciationOf(asset);
  const lifeMonths = Math.round((Number(asset.usefulLifeYears) || 0) * 12);
  const inIdx = monthIdx(asset.inServiceDate || asset.acquisitionDate);
  const entries = [];
  let depreciable = 0;
  (Array.isArray(asset.appreciationEntries) ? asset.appreciationEntries : []).forEach(e => {
    const amount = round2(e && e.amount);
    if (!(amount > 0)) return;
    const idx = monthIdx(e.date);
    const used = inIdx != null && idx != null ? Math.max(0, idx - inIdx) : 0;
    const rem = lifeMonths - used;
    if (lifeMonths > 0 && rem > 0) {
      entries.push({ amount, rem, idx });
      depreciable = round2(depreciable + amount);
    }
  });
  depreciable = Math.min(depreciable, total);
  return { entries, depreciable, nonDepreciable: round2(total - depreciable) };
};

// Appreciation entries with an amount: [{ amount, idx }].
const appreciationEntriesOf = (asset) => (Array.isArray(asset.appreciationEntries) ? asset.appreciationEntries : [])
  .map(e => ({ amount: round2(e && e.amount), idx: monthIdx(e && e.date) }))
  .filter(e => e.amount > 0);

/**
 * The part of an asset's appreciation that can be depreciated: all of it for
 * reducing balance; for straight line only what was dated with life left.
 */
export const depreciableAppreciationOf = (asset) => (
  asset?.depreciationMethod === 'straight_line' ? slAppreciationParts(asset).depreciable : appreciationOf(asset)
);

// Months of a run period (months ending asOfDate) falling in or after month idx.
const monthsFrom = (idx, asOfDate, months) => {
  const asOf = monthIdx(asOfDate);
  if (asOf == null || idx == null) return months;
  const first = Math.max(asOf - months + 1, idx);
  return Math.max(0, asOf - first + 1);
};

/**
 * Depreciation floor for an asset (NBV can never go below this).
 * Straight line with appreciation: appreciation that cannot be depreciated
 * (life already over at its date) stays on top of the salvage value.
 */
export const depreciationFloor = (asset) => {
  const salvage = Number(asset.salvageValue) || 0;
  const floor = asset.depreciationMethod === 'straight_line' ? salvage : Math.max(0, salvage);
  if (asset.depreciationMethod === 'straight_line' && hasAppreciation(asset)) {
    return round2(floor + slAppreciationParts(asset).nonDepreciable);
  }
  return floor;
};

/**
 * Compute depreciation for a single asset for one run period.
 *
 * @param {object} asset  - fixedAssets doc (needs acquisitionCost, accumulatedDepreciation,
 *                          depreciationMethod, depreciationRate, usefulLifeYears, salvageValue,
 *                          inServiceDate/acquisitionDate, status).
 * @param {object} [opts]
 * @param {number} [opts.months=1]   - number of months this run covers (12 for yearly).
 * @param {string|Date} [opts.asOfDate] - the run period end; used for the in-service check.
 * @returns {{ amount:number, opening:number, newAccumulated:number, newNBV:number, fullyDepreciated:boolean, skipped:string|null }}
 */
export const computeDepreciationForPeriod = (asset, opts = {}) => {
  if (hasAppreciation(asset)) return computeWithAppreciation(asset, opts);
  const months = Number(opts.months) > 0 ? Number(opts.months) : 1;
  const cost = Number(asset.acquisitionCost) || 0;
  const accumulated = Number(asset.accumulatedDepreciation) || 0;
  const opening = round2(cost - accumulated); // opening NBV
  const floor = depreciationFloor(asset);

  const base = { amount: 0, opening, newAccumulated: accumulated, newNBV: opening, fullyDepreciated: false, skipped: null };

  if (asset.status === 'Disposed') return { ...base, skipped: 'disposed' };
  if (asset.status === 'Cancelled') return { ...base, skipped: 'cancelled' };
  if (asset.depreciationMethod === 'none') return { ...base, skipped: 'non_depreciable' };
  if (opening <= floor + 0.001) return { ...base, fullyDepreciated: true, skipped: 'fully_depreciated' };
  if (opts.asOfDate && !isInServiceBy(asset, opts.asOfDate)) return { ...base, skipped: 'not_in_service' };

  let amount = 0;
  if (asset.depreciationMethod === 'straight_line') {
    const life = Number(asset.usefulLifeYears) || 0;
    if (life <= 0) return { ...base, skipped: 'no_useful_life' };
    const salvage = Number(asset.salvageValue) || 0;
    const perMonth = (cost - salvage) / (life * 12);
    amount = perMonth * months;
  } else if (asset.depreciationMethod === 'reducing_balance') {
    const rate = Number(asset.depreciationRate) || 0;
    if (rate <= 0) return { ...base, skipped: 'no_rate' };
    // Charge against the opening NBV held flat across the months in the period.
    amount = opening * (rate / 100) * (months / 12);
  } else {
    return { ...base, skipped: 'unknown_method' };
  }

  amount = round2(amount);
  // Clamp so NBV never dips below the floor.
  if (opening - amount < floor) amount = round2(opening - floor);
  if (amount < 0) amount = 0;

  const newAccumulated = round2(accumulated + amount);
  const newNBV = round2(cost - newAccumulated);
  return {
    amount,
    opening,
    newAccumulated,
    newNBV,
    fullyDepreciated: newNBV <= floor + 0.001,
    skipped: amount === 0 ? 'zero' : null,
  };
};

/**
 * Same as computeDepreciationForPeriod, for an asset that has appreciation.
 * Also returns appreciationPart (the share of this charge that falls on the
 * appreciation) and newAppreciationDepreciation.
 */
const computeWithAppreciation = (asset, opts = {}) => {
  const months = Number(opts.months) > 0 ? Number(opts.months) : 1;
  const cost = Number(asset.acquisitionCost) || 0;
  const accumulated = Number(asset.accumulatedDepreciation) || 0;
  const appr = appreciationOf(asset);
  const apprDep = appreciationDepreciationOf(asset);
  const opening = round2(cost + appr - accumulated); // opening book value
  const floor = depreciationFloor(asset);

  const base = {
    amount: 0, opening, newAccumulated: accumulated, newNBV: opening, fullyDepreciated: false, skipped: null,
    appreciationPart: 0, newAppreciationDepreciation: apprDep,
  };

  if (asset.status === 'Disposed') return { ...base, skipped: 'disposed' };
  if (asset.status === 'Cancelled') return { ...base, skipped: 'cancelled' };
  if (asset.depreciationMethod === 'none') return { ...base, skipped: 'non_depreciable' };
  if (opening <= floor + 0.001) return { ...base, fullyDepreciated: true, skipped: 'fully_depreciated' };
  if (opts.asOfDate && !isInServiceBy(asset, opts.asOfDate)) return { ...base, skipped: 'not_in_service' };

  let amount = 0;
  let part = 0;
  if (asset.depreciationMethod === 'straight_line') {
    const life = Number(asset.usefulLifeYears) || 0;
    if (life <= 0) return { ...base, skipped: 'no_useful_life' };
    const salvage = Number(asset.salvageValue) || 0;
    // Cost part: the usual charge, stopping once the cost (less salvage) is used up.
    const baseAmt = ((cost - salvage) / (life * 12)) * months;
    const baseCap = Math.max(0, cost - salvage - (accumulated - apprDep));
    // Appreciation part: each appreciation over the months left at its date.
    const { entries, depreciable } = slAppreciationParts(asset);
    let extra = 0;
    entries.forEach(e => { extra += (e.amount / e.rem) * monthsFrom(e.idx, opts.asOfDate, months); });
    const extraCap = Math.max(0, depreciable - apprDep);
    const b = Math.min(baseAmt, baseCap);
    const x = Math.min(extra, extraCap);
    amount = round2(b + x);
    part = round2(x);
  } else if (asset.depreciationMethod === 'reducing_balance') {
    const rate = Number(asset.depreciationRate) || 0;
    if (rate <= 0) return { ...base, skipped: 'no_rate' };
    // Months of the period before an appreciation's month run on the value
    // without it (matters for catch-up runs and future-dated appreciation).
    let excluded = 0;
    appreciationEntriesOf(asset).forEach(e => {
      const active = monthsFrom(e.idx, opts.asOfDate, months);
      if (active < months) excluded += e.amount * (months - active);
    });
    const weighted = Math.max(0, opening * months - excluded);
    amount = round2(weighted * (rate / 100) / 12);
    // The appreciation's share of the value carries its share of the charge.
    const weightedAppr = Math.max(0, Math.min(weighted, Math.max(0, appr - apprDep) * months - excluded));
    part = weighted > 0 ? round2(amount * (weightedAppr / weighted)) : 0;
  } else {
    return { ...base, skipped: 'unknown_method' };
  }

  // Clamp so the book value never dips below the floor.
  if (opening - amount < floor) amount = round2(opening - floor);
  if (amount < 0) amount = 0;
  const partCap = Math.max(0, round2(depreciableAppreciationOf(asset) - apprDep));
  part = Math.max(0, Math.min(part, amount, partCap));

  const newAccumulated = round2(accumulated + amount);
  const newNBV = round2(cost + appr - newAccumulated);
  return {
    amount,
    opening,
    newAccumulated,
    newNBV,
    fullyDepreciated: newNBV <= floor + 0.001,
    skipped: amount === 0 ? 'zero' : null,
    appreciationPart: part,
    newAppreciationDepreciation: round2(apprDep + part),
    appreciationPartCap: partCap,
    floor,
  };
};

/**
 * Build a depreciation run across many assets for one period.
 *
 * @param {object[]} assets - active fixedAssets docs.
 * @param {object} [opts] - { months, asOfDate } passed through to each asset.
 * @returns {{ lines:object[], totalsByCategory:object, grandTotal:number, depreciableCount:number }}
 *   lines: one row per asset (including skipped ones, with amount 0 + reason).
 *   totalsByCategory: { [categoryId]: { categoryId, categoryName, accumDepAccountId, amount } }
 *   grandTotal: sum of all depreciation (Dr Depreciation Expense).
 */
export const buildDepreciationRun = (assets = [], opts = {}) => {
  const lines = [];
  const totalsByCategory = {};
  let grandTotal = 0;
  let depreciableCount = 0;

  for (const asset of assets) {
    const calc = computeDepreciationForPeriod(asset, opts);
    lines.push({
      assetId: asset.id,
      assetCode: asset.assetCode,
      assetName: asset.assetName,
      categoryId: asset.categoryId,
      categoryName: asset.categoryName,
      accumDepAccountId: asset.accumDepAccountId || null,
      method: asset.depreciationMethod,
      opening: calc.opening,
      amount: calc.amount,
      newNBV: calc.newNBV,
      newAccumulated: calc.newAccumulated,
      fullyDepreciated: calc.fullyDepreciated,
      skipped: calc.skipped,
      ...(hasAppreciation(asset) ? {
        appreciationPart: calc.appreciationPart || 0,
        newAppreciationDepreciation: calc.newAppreciationDepreciation,
        appreciationPartCap: calc.appreciationPartCap != null ? calc.appreciationPartCap : 0,
        floor: depreciationFloor(asset),
      } : {}),
    });

    if (calc.amount > 0) {
      depreciableCount += 1;
      grandTotal = round2(grandTotal + calc.amount);
      const key = asset.categoryId || 'uncategorised';
      if (!totalsByCategory[key]) {
        totalsByCategory[key] = {
          categoryId: asset.categoryId || null,
          categoryName: asset.categoryName || 'Uncategorised',
          accumDepAccountId: asset.accumDepAccountId || null,
          amount: 0,
        };
      }
      totalsByCategory[key].amount = round2(totalsByCategory[key].amount + calc.amount);
    }
  }

  return { lines, totalsByCategory, grandTotal, depreciableCount };
};

/**
 * Year-by-year schedule preview for a single asset (for the detail drawer).
 * Runs until fully depreciated or `maxYears` reached.
 *
 * @returns {{ year:number, opening:number, depreciation:number, closing:number }[]}
 */
export const previewSchedule = (asset, maxYears = 30) => {
  const rows = [];
  if (!asset || asset.depreciationMethod === 'none') return rows;

  const floor = depreciationFloor(asset);
  let accumulated = Number(asset.accumulatedDepreciation) || 0;
  let apprDep = Number(asset.appreciationDepreciation) || 0;

  for (let y = 1; y <= maxYears; y++) {
    const current = { ...asset, accumulatedDepreciation: accumulated, appreciationDepreciation: apprDep };
    const opening = bookValueOf(current);
    if (opening <= floor + 0.001) break;
    const calc = computeDepreciationForPeriod(current, { months: 12 });
    if (calc.amount <= 0) break;
    accumulated = calc.newAccumulated;
    if (calc.newAppreciationDepreciation != null) apprDep = calc.newAppreciationDepreciation;
    rows.push({
      year: y,
      opening,
      depreciation: calc.amount,
      closing: calc.newNBV,
    });
    if (calc.fullyDepreciated) break;
  }
  return rows;
};

/**
 * Compute the gain/loss on disposing an asset.
 * gain (+) when proceeds exceed net book value; loss (−) otherwise.
 *
 * @returns {{ nbv:number, gainLoss:number, isGain:boolean }}
 */
export const computeDisposalResult = (asset, proceeds = 0) => {
  const nbv = bookValueOf(asset); // cost + appreciation − accumulated depreciation
  const gainLoss = round2((Number(proceeds) || 0) - nbv);
  return { nbv, gainLoss, isGain: gainLoss >= 0 };
};
